import {
  ChangeDetectorRef,
  inject,
  OnDestroy,
  Pipe,
  PipeTransform,
} from '@angular/core';
import dayjs from 'dayjs';

@Pipe({
  name: 'dateEval',
  pure: false,
})
export class DateEvalPipe implements PipeTransform, OnDestroy {
  timeoutRef?: ReturnType<typeof setTimeout>;

  intervalRef?: ReturnType<typeof setTimeout>;

  private showElement = false;

  private value: { from: dayjs.Dayjs; to: dayjs.Dayjs } | undefined;

  private cd = inject(ChangeDetectorRef);

  transform(value: {
    from: dayjs.Dayjs | string;
    to: dayjs.Dayjs | string;
  }): boolean {
    if (!value || !value.from || !value.to) {
      this.clear();
      return false;
    }
    this.value = { from: dayjs(value.from), to: dayjs(value.to) };

    const now = dayjs();

    // If `to` has already passed, it will never be true in the future
    if (
      !this.value ||
      !this.value.from ||
      !this.value.to ||
      this.value.to.isBefore(now)
    ) {
      this.clear();
      return false;
    }

    this.runInnerCheck();

    return this.showElement;
  }

  private clear(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
    }
  }

  private runInnerCheck(): void {
    if (!this.value) {
      return;
    }
    const visible = this.isVisible();
    if (visible !== this.showElement) {
      this.showElement = visible;
      this.cd.markForCheck();
      return;
    }
    const now = dayjs();
    const fromDiff = this.value.from.diff(now, 'millisecond');
    const toDiff = this.value.to.diff(now, 'millisecond');
    const fromPassed = fromDiff === 0 || now.isAfter(this.value.from);
    this.showElement = this.isVisible();

    // If `from` has already passed, we need to check if `to` is in the far future, or we need to wait for the status switch
    if (fromPassed && toDiff >= 5000) {
      this.scheduleTimeout(toDiff - 5000);
    } else if (fromPassed) {
      this.scheduleInterval();
    }

    // If `from` is in the future, we need to check if it's in the far future, or we need to wait for the status switch
    if (now.isBefore(this.value.from) && fromDiff >= 5000) {
      this.scheduleTimeout(fromDiff - 5000);
    } else if (now.isBefore(this.value.from)) {
      this.scheduleInterval();
    }
  }

  ngOnDestroy() {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
    }
  }

  private isVisible(): boolean {
    if (!this.value || !this.value.from || !this.value.to) {
      return false;
    }
    const now = dayjs();
    return (
      now.isSame(this.value.from) ||
      now.isSame(this.value.to) ||
      (now.isAfter(this.value.from) && now.isBefore(this.value.to))
    );
  }

  private scheduleInterval() {
    if (!this.intervalRef) {
      this.intervalRef = setInterval(() => {
        const visible = this.isVisible();
        if (visible !== this.showElement) {
          this.showElement = visible;
          clearInterval(this.intervalRef);
          this.runInnerCheck();
        }
      }, 500);
    }
  }

  private scheduleTimeout(ms: number) {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
    this.timeoutRef = setTimeout(() => {
      this.runInnerCheck();
    }, ms);
  }
}
